/**
 * BSR Trend Chart Component
 * Shows BSR (Best Seller Rank) history over time using Recharts
 * 
 * Data sources:
 * - Primary: bsr_history collection (SP-API daily snapshots)
 * - Optional: Keepa API (historical data when connected)
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { config } from '../../config/environment';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  TrendingUp, 
  TrendingDown, 
  RefreshCw, 
  Download, 
  Loader2,
  AlertCircle,
  BarChart3
} from 'lucide-react';

const API = config.apiBaseUrl;
const MOCK_TENANT_ID = 'tenant-123';

// Format BSR for display
const formatBSR = (value) => {
  if (!value && value !== 0) return 'N/A';
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toLocaleString();
};

// Custom tooltip for the chart
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-gray-900 border border-gray-700 rounded-lg p-2 shadow-lg">
        <p className="text-xs text-gray-400 mb-1">{label}</p>
        <p className="text-sm font-bold text-white">
          #{formatBSR(data.bsr)}
        </p>
        {data.source && (
          <p className="text-[10px] text-gray-500 mt-1">
            Source: {data.source === 'keepa' ? 'Keepa' : 'Amazon SP-API'}
          </p>
        )}
      </div>
    );
  }
  return null;
};

const BSRTrendChart = ({ 
  productId, 
  asin, 
  height = 200,
  showKeepaButton = true 
}) => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingKeepa, setLoadingKeepa] = useState(false);
  const [error, setError] = useState(null);
  const [keepaConnected, setKeepaConnected] = useState(false);
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState({ min: null, max: null, avg: null, trend: null });

  // Check if Keepa is connected
  const checkKeepaStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/keepa/status`, {
        params: { tenant_id: MOCK_TENANT_ID }
      });
      setKeepaConnected(response.data.connected);
    } catch (err) {
      setKeepaConnected(false);
    }
  }, []);

  // Fetch BSR history from our SP-API snapshots
  const fetchBSRHistory = useCallback(async () => {
    if (!productId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/bsr/product/${productId}/history`, {
        params: { tenant_id: MOCK_TENANT_ID, days }
      });
      
      if (response.data.success && response.data.history) {
        const formattedData = response.data.history.map(item => ({
          date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          bsr: item.main_category_bsr,
          source: 'sp-api'
        })).filter(item => item.bsr !== null && item.bsr !== undefined);
        
        setChartData(formattedData);
        calculateStats(formattedData);
      }
    } catch (err) {
      console.error('BSR history fetch error:', err);
      setError(err.response?.data?.detail || 'Failed to load BSR history');
    } finally {
      setLoading(false);
    }
  }, [productId, days]);

  // Fetch extended history from Keepa
  const fetchKeepaHistory = useCallback(async () => {
    if (!asin) return;
    
    setLoadingKeepa(true);
    
    try {
      const response = await axios.get(`${API}/keepa/bsr-history/${asin}`, {
        params: { tenant_id: MOCK_TENANT_ID, days }
      });
      
      if (response.data.success && response.data.history) {
        const formattedData = response.data.history.map(item => ({
          date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          bsr: item.bsr,
          source: item.source
        })).filter(item => item.bsr !== null && item.bsr !== undefined);
        
        // Merge and dedupe by date
        const mergedData = [...formattedData];
        const seen = new Set();
        const dedupedData = mergedData.filter(item => {
          const key = `${item.date}-${item.bsr}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        
        setChartData(dedupedData);
        calculateStats(dedupedData);
      }
    } catch (err) {
      console.error('Keepa history fetch error:', err);
      // Don't show error for Keepa - just fall back to SP-API data
    } finally {
      setLoadingKeepa(false);
    }
  }, [asin, days]);

  // Calculate statistics from chart data
  const calculateStats = (data) => {
    if (!data || data.length === 0) {
      setStats({ min: null, max: null, avg: null, trend: null });
      return;
    }
    
    const bsrValues = data.map(d => d.bsr).filter(v => v !== null && v !== undefined);
    
    if (bsrValues.length === 0) {
      setStats({ min: null, max: null, avg: null, trend: null });
      return;
    }
    
    const min = Math.min(...bsrValues);
    const max = Math.max(...bsrValues);
    const avg = Math.round(bsrValues.reduce((a, b) => a + b, 0) / bsrValues.length);
    
    // Calculate trend (first vs last - lower BSR is better)
    const first = bsrValues[0];
    const last = bsrValues[bsrValues.length - 1];
    const trend = first && last ? ((first - last) / first * 100) : 0;
    
    setStats({ min, max, avg, trend });
  };

  // Initial load
  useEffect(() => {
    checkKeepaStatus();
    fetchBSRHistory();
  }, [checkKeepaStatus, fetchBSRHistory]);

  // No ASIN configured
  if (!asin) {
    return null; // BSRSection already shows the warning
  }

  // Loading state
  if (loading && chartData.length === 0) {
    return (
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          <span className="ml-2 text-sm text-gray-400">Loading BSR history...</span>
        </CardContent>
      </Card>
    );
  }

  // No data state
  if (!loading && chartData.length === 0) {
    return (
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-xs font-medium text-gray-400 uppercase tracking-wide flex items-center gap-2">
            <BarChart3 className="w-3 h-3" />
            BSR Trend
          </CardTitle>
        </CardHeader>
        <CardContent className="py-6">
          <div className="text-center">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 text-gray-600" />
            <p className="text-sm text-gray-400 mb-2">No BSR history available</p>
            <p className="text-xs text-gray-500">
              Click &quot;Refresh&quot; in BSR section above to start tracking
            </p>
            {showKeepaButton && keepaConnected && (
              <Button
                variant="outline"
                size="sm"
                onClick={fetchKeepaHistory}
                disabled={loadingKeepa}
                className="mt-3 text-xs"
              >
                {loadingKeepa ? (
                  <Loader2 className="w-3 h-3 animate-spin mr-1" />
                ) : (
                  <Download className="w-3 h-3 mr-1" />
                )}
                Load from Keepa
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gray-800 border-gray-700">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xs font-medium text-gray-400 uppercase tracking-wide flex items-center gap-2">
            <BarChart3 className="w-3 h-3" />
            BSR Trend ({days} Days)
          </CardTitle>
          
          <div className="flex items-center gap-2">
            {/* Date range selector */}
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="h-6 text-[10px] bg-gray-700 border border-gray-600 text-white rounded px-2"
            >
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
              <option value={180}>6 months</option>
              <option value={365}>1 year</option>
            </select>
            
            {/* Keepa button */}
            {showKeepaButton && keepaConnected && (
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchKeepaHistory}
                disabled={loadingKeepa}
                className="h-6 text-[10px] text-purple-400 hover:text-purple-300"
                title="Load full history from Keepa"
              >
                {loadingKeepa ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <>
                    <Download className="w-3 h-3 mr-1" />
                    Keepa
                  </>
                )}
              </Button>
            )}
            
            {/* Refresh button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchBSRHistory}
              disabled={loading}
              className="h-6 text-[10px] text-gray-400 hover:text-gray-300"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        
        {/* Stats row */}
        <div className="flex items-center gap-4 mt-2">
          {stats.trend !== null && (
            <div className="flex items-center gap-1">
              {stats.trend > 0 ? (
                <TrendingUp className="w-3 h-3 text-green-400" />
              ) : stats.trend < 0 ? (
                <TrendingDown className="w-3 h-3 text-red-400" />
              ) : null}
              <span className={`text-xs font-medium ${
                stats.trend > 0 ? 'text-green-400' : 
                stats.trend < 0 ? 'text-red-400' : 'text-gray-400'
              }`}>
                {stats.trend > 0 ? '+' : ''}{stats.trend.toFixed(1)}%
              </span>
              <span className="text-[10px] text-gray-500">
                {stats.trend > 0 ? 'improved' : stats.trend < 0 ? 'declined' : 'stable'}
              </span>
            </div>
          )}
          
          {stats.min !== null && (
            <Badge variant="outline" className="text-[10px] text-green-400 border-green-700">
              Best: #{formatBSR(stats.min)}
            </Badge>
          )}
          
          {stats.avg !== null && (
            <Badge variant="outline" className="text-[10px] text-gray-400">
              Avg: #{formatBSR(stats.avg)}
            </Badge>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="pt-2">
        {error && (
          <div className="text-xs text-red-400 mb-2 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {error}
          </div>
        )}
        
        <div style={{ height }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
            >
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="#374151" 
                vertical={false}
              />
              <XAxis 
                dataKey="date" 
                tick={{ fill: '#9CA3AF', fontSize: 10 }}
                tickLine={{ stroke: '#4B5563' }}
                axisLine={{ stroke: '#4B5563' }}
              />
              <YAxis 
                tick={{ fill: '#9CA3AF', fontSize: 10 }}
                tickLine={{ stroke: '#4B5563' }}
                axisLine={{ stroke: '#4B5563' }}
                tickFormatter={(value) => `#${formatBSR(value)}`}
                reversed={true} // Lower BSR is better, show at top
                domain={['dataMin - 100', 'dataMax + 100']}
              />
              <Tooltip content={<CustomTooltip />} />
              
              {/* Average reference line */}
              {stats.avg && (
                <ReferenceLine 
                  y={stats.avg} 
                  stroke="#6B7280" 
                  strokeDasharray="5 5"
                  label={{ 
                    value: 'Avg', 
                    fill: '#6B7280', 
                    fontSize: 10,
                    position: 'right'
                  }}
                />
              )}
              
              <Line
                type="monotone"
                dataKey="bsr"
                stroke="#3B82F6"
                strokeWidth={2}
                dot={{ fill: '#3B82F6', strokeWidth: 0, r: 3 }}
                activeDot={{ fill: '#60A5FA', strokeWidth: 0, r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        
        {/* Data source indicator */}
        <div className="flex items-center justify-end gap-2 mt-2">
          <span className="text-[9px] text-gray-500">
            {chartData.length} data points
          </span>
          {chartData.some(d => d.source === 'keepa') && (
            <Badge variant="outline" className="text-[9px] text-purple-400 border-purple-700">
              Includes Keepa data
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default BSRTrendChart;
